import React, { useContext, useEffect, useState } from 'react'
import { Collapse, Paper, useTheme } from '@mui/material'
import clsx from 'clsx'
import delay from 'delay'
import moment from 'moment'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import Drawer from '@mui/material/Drawer'
import Tooltip from '@mui/material/Tooltip'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import ErrorIcon from '@mui/icons-material/Error'
import SubtitlesIcon from '@mui/icons-material/Subtitles'
import PaymentsIcon from '@mui/icons-material/Payments'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import DeleteIcon from '@mui/icons-material/Delete'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckIcon from '@mui/icons-material/Check'
import LaunchIcon from '@mui/icons-material/Launch'
import messagesLib from '../../libs/messages'
import { getMessageStatus } from '../../libs/messageStatus'
import { TemplatePreviewBubble } from '../TemplatePreviewBubble'
import { HiperPayPreview } from '../HiperPayPreview'
import { translateMetaError } from '../../libs/metaErrors'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import hipersendService from '../../services/hipersend'
import Text from '../Text'
import ConfirmationModal from '../ConfirmationModal'
import LinearProgressCustom from '../LinearProgressCustom'
import WpBgLight from '../../assets/wp-bg-light.png'
import WpBgDark from '../../assets/wp-bg-dark.png'
import { linkCode, isLinkAbrivel } from '../../libs/hiperpayLink'
import { WabaTemplatesContext, HiperPayContext } from './context'
import { GRADE_COLUNAS } from './colunas'
import MessageStatusChip, { corDaSeveridade } from './MessageStatusChip'
import MessageErrorInline, { temAvisoInline } from './MessageErrorInline'
import MetaErrorLinks from './MetaErrorLinks'
import MessagePeek from './MessagePeek'
import MessageQuickActions from './MessageQuickActions'
import TemplateCategoryBadge from './TemplateCategoryBadge'
import CustoDoEnvio from './CustoDoEnvio'

export default function MessageListItem({ message, onResendMessage, onDeleteMessage, densidade = 'detalhado' }) {
  const { getCredentials } = useContext(ApiDataWrapperContext)
  const { api, secret } = getCredentials()
  const theme = useTheme()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [file, setFile] = useState(messagesLib.getFileFromMessage(message?.message || '') || undefined)
  const [messageFormatted, setMessageFormatted] = useState('')
  const [messageToResend, setMessageToResend] = useState(undefined)
  const [messageToDelete, setMessageToDelete] = useState(undefined)
  const [isOpenConfirmationModalResendMessage, setIsOpenConfirmationModalResendMessage] = useState(false)
  const [isOpenConfirmationModalDeleteMessage, setIsOpenConfirmationModalDeleteMessage] = useState(false)
  // O drawer do payload mora aqui, no item, e não mais dentro do expandir: assim
  // o chip da linha e o botão "VER PAYLOAD" de dentro abrem o mesmo drawer, sem
  // duplicar conteúdo nem obrigar a expandir para chegar nele.
  const [isPayloadOpen, setIsPayloadOpen] = useState(false)
  const [isCopied, setIsCopied] = useState(false)

  // A regra de "entregue apesar do erro" (o chamado I007) vive em libs/messageStatus.js.
  const estado = getMessageStatus(message)

  // O wrapper da sub-linha precisa saber ANTES de renderizar se existe aviso. O
  // MessageErrorInline já devolve null quando não há nada a dizer, mas um <div>
  // vazio continua abrindo uma faixa dentro da grade e consumindo o espaçamento —
  // era exatamente esse o buraco que deixava a lista "quadrada" no modo compacto.
  // A lista de estados com aviso mora no próprio MessageErrorInline.
  const temAviso = temAvisoInline(message)

  // Na tabela o horário aparece em duas linhas curtas (hora em cima, data
  // embaixo); a data/hora completa, com segundos, fica na dica do mouse — é ela
  // que o suporte usa pra cruzar com o log.
  const criadoEm = moment(message.created_at)
  const criadoEmCompleto = criadoEm.format('DD/MM/YYYY [às] HH:mm:ss')

  const handleResendMessage = async () => {
    try {
      setIsLoading(true)
      await delay(1000)
      await hipersendService.notificacoesInteligentes.resendMessage(messageToResend._id, api, secret)
      setMessageToResend(undefined)
      onResendMessage()
    } catch (error) {
      console.error(error)
      setMessageToResend(undefined)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteMessage = async () => {
    try {
      await hipersendService.notificacoesInteligentes.deleteMessage(messageToDelete._id, api, secret)
      setMessageToDelete(undefined)
      onDeleteMessage()
    } catch (error) {
      console.error(error)
      setMessageToDelete(undefined)
    }
  }

  useEffect(() => {
    if(!messageToDelete) return
    setIsOpenConfirmationModalDeleteMessage(true)
  }, [messageToDelete])

  useEffect(() => {
    setMessageFormatted(messagesLib.formatMessage(message.message, { link: message.link }))
  }, [])

  useEffect(() => {
    if(!messageToResend) return
    handleResendMessage()
  }, [messageToResend])

  return (
    <>
      <ConfirmationModal
        open={isOpenConfirmationModalResendMessage}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Reenviar mensagem
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra REENVIAR para prosseguir com o reenvio da mensagem"
        challengeAwnswer="REENVIAR"
        onClose={() => {
          setIsOpenConfirmationModalResendMessage(false)
          setMessageToResend(undefined)
        }}
        onConfirm={handleResendMessage}
      >
        <Text>Deseja realmente reenviar essa mensagem?</Text>
      </ConfirmationModal>
      <ConfirmationModal
        open={isOpenConfirmationModalDeleteMessage}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Deletar mensagem
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra DELETAR para prosseguir com a remoção da mensagem"
        challengeAwnswer="DELETAR"
        onClose={() => {
          setIsOpenConfirmationModalDeleteMessage(false)
          setMessageToDelete(undefined)
        }}
        onConfirm={handleDeleteMessage}
      >
        <Text className="font-bold">Essa ação é irreversível.</Text>
        <Text>Deseja realmente deletar essa mensagem?</Text>
      </ConfirmationModal>
      <Drawer
        anchor="right"
        open={isPayloadOpen}
        onClose={() => setIsPayloadOpen(false)}
      >
        <div className="w-[750px] h-screen p-8">
          <div className="mb-4 flex justify-between">
            <button onClick={() => setIsPayloadOpen(false)} className="flex items-center space-x-2">
              <ChevronLeftIcon />
              <span className="text-xs font-medium">FECHAR</span>
            </button>
            <CopyToClipboard
              text={message.message}
              onCopy={async () => {
                setIsCopied(true)
                await delay(1000)
                setIsCopied(false)
              }}
            >
              <button
                disabled={isCopied}
                className="flex items-center space-x-2"
              >
                {
                  isCopied
                  ? <CheckIcon style={{ fontSize: 18 }} />
                  : <ContentCopyIcon style={{ fontSize: 18 }} />
                }
                <span className="text-xs font-medium">
                  {
                    isCopied
                    ? 'COPIADO'
                    : 'COPIAR CONTEÚDO'
                  }
                </span>
              </button>
            </CopyToClipboard>
          </div>
          <textarea className="w-full h-full bg-[#7e7e7e12] p-4 font-medium" disabled>{ message?.message }</textarea>
        </div>
      </Drawer>
      <li className="relative">
        <span
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0 w-[2px]"
          style={{ backgroundColor: corDaSeveridade(estado.severidade, theme) }}
        />
        <div className="absolute top-0 left-0 w-full">
          <Collapse in={isLoading} timeout="auto" unmountOnExit>
            <LinearProgressCustom />
          </Collapse>
        </div>
        <div>
          {/* Linha da tabela (md+). A grade vem de colunas.js porque o cabeçalho
              da lista usa exatamente a mesma — os rótulos "Telefone" e "Criado
              em", que antes se repetiam nas 30 linhas, agora são escritos uma
              única vez lá em cima. */}
          <div
            className="hidden lg:grid items-center content-center gap-x-3 min-h-[46px] px-[14px] transition-colors hover:bg-gray-500/5"
            style={{ gridTemplateColumns: GRADE_COLUNAS }}
          >
            <BotaoExpandir aberto={isExpanded} onClick={() => setIsExpanded(!isExpanded)} />

            {/* O chip é `inline-flex`: solto na grade ele seria "blocado" e a
                pílula esticaria até o fim da coluna. O div segura o formato. */}
            <div className="min-w-0">
              <MessageStatusChip message={message} />
            </div>

            <Destinatario phone={message.phone} />

            <div className="min-w-0">
              <TemplateCategoryBadge message={message} />
            </div>

            <div className="min-w-0">
              <MessagePeek message={message} />
            </div>

            {/* Todo componente de célula fica dentro de um div SEMPRE presente:
                na grade os itens são posicionados por ordem de chegada, então uma
                célula que se some (mensagem simples não tem atalho nenhum) puxaria
                as colunas seguintes uma casa pra esquerda — e aí o horário
                apareceria embaixo do rótulo "Ações". */}
            <div className="min-w-0">
              <MessageQuickActions message={message} onVerPayload={() => setIsPayloadOpen(true)} />
            </div>

            {/* Horário + o ⓘ do custo do envio (só aparece quando o master manda
                o custo — API Oficial). O ícone vem antes pra o horário seguir
                encostado na direita, alinhado com o rótulo "Envio". */}
            <div className="flex items-center justify-end gap-1.5">
              <CustoDoEnvio message={message} />
              <div className="text-right leading-[1.15]" title={criadoEmCompleto}>
                <span
                  className="block text-[12.5px] font-semibold"
                  style={{ fontVariantNumeric: 'tabular-nums', color: theme.palette.text.primary }}
                >
                  {criadoEm.format('HH:mm')}
                </span>
                <span
                  className="block text-[11px]"
                  style={{ fontVariantNumeric: 'tabular-nums', color: theme.isDark ? '#a2a7b0' : '#525e6b' }}
                >
                  {criadoEm.format('DD/MM')}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-0.5">
              <BotaoDaLinha titulo="Reenviar" disabled={isLoading} onClick={() => setMessageToResend(message)}>
                <RefreshIcon style={{ fontSize: 16 }} />
              </BotaoDaLinha>
              <BotaoDaLinha titulo="Apagar" disabled={isLoading} onClick={() => setMessageToDelete(message)}>
                <DeleteIcon style={{ fontSize: 16 }} />
              </BotaoDaLinha>
            </div>

            {/* Sub-linha do aviso: começa alinhada ao destinatário e vai até o
                fim. Só existe no modo detalhado — no compacto a linha fica com
                uma faixa só, e o motivo continua ao alcance pela faixa de
                severidade na borda e pela dica do chip de status. */}
            {densidade === 'detalhado' && temAviso && (
              <div className="pb-2" style={{ gridColumn: '3 / -1' }}>
                <MessageErrorInline message={message} />
              </div>
            )}
          </div>

          {/* Tela estreita: grade de 8 colunas não cabe, então a linha vira
              pilha. O TemplateCategoryBadge vem com o nome escrito porque aqui
              não existe hover — o tooltip que guarda o nome do template no
              desktop simplesmente não acontece em tela de toque. */}
          {/* Abaixo de lg a tabela não cabe: as colunas fixas somam ~672px e a
              coluna da mensagem ficaria com uns 60px — inútil. Nessa faixa a
              linha vira pilha, que usa a largura toda pra cada informação. */}
          <div className="lg:hidden flex flex-col gap-1.5 px-[13px] py-[11px]">
            <div className="flex items-center gap-2">
              <BotaoExpandir aberto={isExpanded} onClick={() => setIsExpanded(!isExpanded)} />
              <MessageStatusChip message={message} />
              <Destinatario phone={message.phone} />
              <span className="ml-auto flex-none">
                <CustoDoEnvio message={message} />
              </span>
              <span
                className="flex-none whitespace-nowrap text-[11px]"
                style={{ fontVariantNumeric: 'tabular-nums', color: theme.isDark ? '#a2a7b0' : '#525e6b' }}
                title={criadoEmCompleto}
              >
                <span
                  className="text-[12.5px] font-semibold"
                  style={{ color: theme.palette.text.primary }}
                >
                  {criadoEm.format('HH:mm')}
                </span>
                {` · ${criadoEm.format('DD/MM')}`}
              </span>
            </div>

            <TemplateCategoryBadge message={message} mostrarNome />
            <MessagePeek message={message} />
            {densidade === 'detalhado' && temAviso && <MessageErrorInline message={message} />}

            <div className="flex items-center gap-2">
              <MessageQuickActions message={message} onVerPayload={() => setIsPayloadOpen(true)} />
              <div className="ml-auto flex items-center gap-0.5">
                <BotaoDaLinha titulo="Reenviar" disabled={isLoading} onClick={() => setMessageToResend(message)}>
                  <RefreshIcon style={{ fontSize: 16 }} />
                </BotaoDaLinha>
                <BotaoDaLinha titulo="Apagar" disabled={isLoading} onClick={() => setMessageToDelete(message)}>
                  <DeleteIcon style={{ fontSize: 16 }} />
                </BotaoDaLinha>
              </div>
            </div>
          </div>

          {/* O recuo saiu da grade da linha e entrou pra dentro do Collapse: com
              ele por fora, toda linha fechada reservaria o espaço à toa. */}
          <div className="px-[14px]">
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <div className="pb-4">
                {
                  estado.ehFalha &&
                  <MessageErrorBox erro={message.erro} />
                }
                <div className="py-8">
                  <div className="grid grid-cols-3">
                    <div>
                      {
                        message.ack >= 1
                        ? <ProgressDone />
                        : <ProgressWaiting />
                      }
                      <Text className="font-bold text-center pt-6">
                        Recebida no servidor
                      </Text>
                    </div>
                    <div>
                      {
                        message.ack >= 2
                        ? <ProgressDone />
                        : <ProgressWaiting />
                      }
                      <Text className="hidden md:block font-bold text-center pt-6">
                        Mensagem recebida no device
                      </Text>
                      <Text className="block md:hidden font-bold text-center pt-6">
                        Recebida
                      </Text>
                      {
                        message.receivedAt &&
                        <Text className="text-center">
                          {moment(message.receivedAt).format('DD/MM/YYYY [às] HH:mm:ss')}
                        </Text>
                      }
                    </div>
                    <div>
                      {
                        message.ack >= 3
                        ? <ProgressDone />
                        : <ProgressWaiting />
                      }
                      <Text className="hidden md:block font-bold text-center pt-6">
                        Mensagem lida
                      </Text>
                      <Text className="block md:hidden font-bold text-center pt-6">
                        Lida
                      </Text>
                      {
                        message.readAt &&
                        <Text className="text-center">
                          {moment(message.readAt).format('DD/MM/YYYY [às] HH:mm:ss')}
                        </Text>
                      }
                    </div>
                  </div>
                </div>
                {
                  file &&
                  <div className={clsx('flex items-center space-x-2 mb-4 p-2 rounded bg-opacity-[0.1]', {
                    'bg-white': theme.isDark,
                    'bg-gray-800': !theme.isDark
                  })}>
                    <InsertDriveFileIcon style={{ fontSize: 30, opacity: 0.5 }} />
                    <div>
                      <span className="block font-ligh text-sm">{file.filename}</span>
                      <a className="block font-medium" href={sanitizeSgpFileUrl(file.url)} target="_blank" rel="noreferrer">{sanitizeSgpFileUrl(file.url)}</a>
                    </div>
                  </div>
                }
                <Paper elevation={8}>
                  <div
                    className="p-4 md:p-12 space-y-6"
                    style={{
                      backgroundImage: theme.isDark ? `url(${WpBgDark})` : `url(${WpBgLight})`
                    }}
                  >
                    {/* message */}
                    { messageFormatted && <MessageSimple message={message} onVerPayload={() => setIsPayloadOpen(true)} /> }
                    {/* file */}
                    { file && <MessageFile file={file} /> }
                  </div>
                </Paper>
              </div>
            </Collapse>
          </div>
        </div>
      </li>
    </>
  )
}

// Chevron de abrir/fechar a linha. Vive num componente só porque a tabela e a
// pilha da tela estreita usam o mesmo botão — duplicar significaria uma das duas
// versões ficar pra trás no próximo ajuste.
function BotaoExpandir({ aberto, onClick }) {
  const theme = useTheme()

  return (
    <button
      type="button"
      aria-label={aberto ? 'Recolher detalhes' : 'Expandir detalhes'}
      aria-expanded={aberto}
      onClick={onClick}
      className="grid h-[24px] w-[24px] flex-none place-items-center rounded"
      style={{ color: theme.isDark ? '#767c86' : '#85909c' }}
    >
      <ExpandMoreIcon
        style={{
          fontSize: 18,
          transition: 'transform .15s',
          transform: aberto ? 'rotate(180deg)' : 'none',
        }}
      />
    </button>
  )
}

// Coluna do destinatário: só o número, sem rótulo (ele agora está no cabeçalho).
// `tabular-nums` deixa os dígitos com a mesma largura, então os números se
// alinham verticalmente de linha em linha e a coluna dá pra ler de cima a baixo.
// Grupo perdeu o rótulo escrito, mas continua reconhecível na dica do mouse.
function Destinatario({ phone }) {
  const theme = useTheme()
  const ehGrupo = String(phone || '').length > 16

  return (
    <span
      className="truncate text-[13.5px] font-medium"
      style={{ fontVariantNumeric: 'tabular-nums', color: theme.palette.text.primary }}
      title={ehGrupo ? `Grupo · ${phone}` : phone}
    >
      {phone}
    </span>
  )
}

// Botão de ícone da linha, na mesma medida dos atalhos do MessageQuickActions
// (27×27). Os círculos de 40px de antes ocupavam mais altura que a informação da
// própria linha. O <span> em volta existe porque o Tooltip precisa de um nó que
// receba os eventos do mouse, e um <button disabled> não dispara nenhum.
function BotaoDaLinha({ titulo, disabled, onClick, children }) {
  const theme = useTheme()

  return (
    <Tooltip title={titulo}>
      <span>
        <button
          type="button"
          aria-label={titulo}
          disabled={disabled}
          onClick={onClick}
          className="grid h-[27px] w-[27px] place-items-center rounded-md transition-colors hover:bg-gray-500/10 disabled:opacity-30"
          style={{ color: theme.isDark ? '#767c86' : '#85909c' }}
        >
          {children}
        </button>
      </span>
    </Tooltip>
  )
}

function Message({ children, isFile }) {
  const theme = useTheme()

  return (
    <div
      className={clsx('h-full table rounded p-2 md:p-4 whitespace-pre-line', {
        'shadow-md bg-[#005c4bff]': theme.isDark,
        'bg-[#ecebeb]': !theme.isDark,
        'md:max-w-[60%]': !isFile,
        'md:max-w-[40%]': isFile
      })}
    >
      {children}
    </div>
  )
}

function MessageSimple({ message, onVerPayload }) {
  const paymentLinkThumbnail = messagesLib.getPaymentLinkThumbnail(message.message)
  const hiperPayData = messagesLib.getHiperPayData(message.message)
  const { profilesById, banners } = useContext(HiperPayContext)

  const bubble = (
    <div>
      <Message>
        {
          paymentLinkThumbnail &&
          <div className="w-full mb-2">
            <img
              src={paymentLinkThumbnail}
              className="rounded-md"
            />
          </div>
        }
        {
          message.isWhatsappMessageTemplate
          ?
          <MessageTemplatePreviewHiperApiV3 message={message} />
          :
          messagesLib.formatMessage(message.message, { link: message.link })
        }
      </Message>
      {
        message.isWhatsappMessageTemplate &&
        <MessageSimpleShowPayloadHiperApiV3 onVerPayload={onVerPayload} />
      }
    </div>
  )

  // Sem link de pagamento: só a mensagem, como antes.
  if (!hiperPayData) return bubble

  // Com link HiperPay: mensagem à esquerda, vitrine reconstruída à direita
  // (empilha no mobile). A vitrine usa branding/banners do contexto.
  const profile = hiperPayData.profileId ? profilesById[String(hiperPayData.profileId)] : undefined

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
      <div>
        <Text className="font-light text-xs mb-2">MENSAGEM WHATSAPP</Text>
        {bubble}
      </div>
      <div>
        <Text className="font-light text-xs mb-2">PÁGINA DE PAGAMENTO (HIPERPAY)</Text>
        <HiperPayPreview data={hiperPayData} profile={profile} banners={banners} link={message.link} />
        <HiperPayLinkBar link={message.link} isTemplate={message.isWhatsappMessageTemplate} />
      </div>
    </div>
  )
}

// Endereço da página de pagamento, por extenso. A barra do preview mostra
// host/código truncado — o que serve pra reconhecer a página, mas não pra
// investigar: quando o cliente diz que o link não abriu, quem atende precisa do
// endereço inteiro na mão, pra abrir e pra colar no chamado.
function HiperPayLinkBar({ link, isTemplate }) {
  const theme = useTheme()
  const [isCopied, setIsCopied] = useState(false)
  const abrivel = isLinkAbrivel(link)
  const codigo = linkCode(link)

  useEffect(() => {
    if (!isCopied) return
    const t = setTimeout(() => setIsCopied(false), 2000)
    return () => clearTimeout(t)
  }, [isCopied])

  // Sem link salvo não há o que investigar aqui — mas o silêncio faria parecer
  // que a mensagem saiu sem pagamento. Dizemos o que aconteceu.
  if (!link) {
    return (
      <div
        className="mt-3 px-3 py-2 rounded-lg text-xs opacity-70"
        style={{ border: theme.isDark ? '1px dashed #334155' : '1px dashed #cbd5e1' }}
      >
        <Text component="span" className="text-xs">
          {
            isTemplate
            ? 'O endereço da página não foi guardado nesta mensagem — só passou a ser salvo nos envios da API Oficial depois da atualização.'
            : 'Esta mensagem não tem endereço de página de pagamento salvo.'
          }
        </Text>
      </div>
    )
  }

  return (
    <div
      className="mt-3 rounded-lg overflow-hidden"
      style={{ border: theme.isDark ? '1px solid #334155' : '1px solid #e2e8f0' }}
    >
      <div className="px-3 pt-2 flex items-center justify-between gap-2">
        <Text component="span" className="text-[10px] font-semibold uppercase tracking-wider opacity-50">
          Endereço da página
        </Text>
        {codigo && (
          <Text component="span" className="text-[10px] opacity-50">
            código <b>{codigo}</b>
          </Text>
        )}
      </div>

      {/* break-all: o código é uma sequência longa sem espaço; sem isso ele
          estoura a coluna em vez de quebrar. */}
      <div className="px-3 pb-2">
        <Text component="span" className="text-xs font-mono break-all select-all leading-snug">
          {link}
        </Text>
      </div>

      <div
        className="flex items-center gap-1 px-2 py-1.5"
        style={{ borderTop: theme.isDark ? '1px solid #334155' : '1px solid #e2e8f0' }}
      >
        <CopyToClipboard text={link} onCopy={() => setIsCopied(true)}>
          <button type="button" className="flex items-center gap-1 px-2 py-1 rounded opacity-80 hover:opacity-100">
            {
              isCopied
              ? <CheckIcon style={{ fontSize: 15, color: '#059669' }} />
              : <ContentCopyIcon style={{ fontSize: 15 }} />
            }
            <span className="text-[11px] font-semibold">{isCopied ? 'COPIADO' : 'COPIAR LINK'}</span>
          </button>
        </CopyToClipboard>

        {abrivel && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1 px-2 py-1 rounded opacity-80 hover:opacity-100"
          >
            <LaunchIcon style={{ fontSize: 15 }} />
            <span className="text-[11px] font-semibold">ABRIR</span>
          </a>
        )}
      </div>
    </div>
  )
}

// Mostra primeiro a EXPERIÊNCIA de quem recebe. Quando o template Meta está no
// snapshot, renderiza a bolha rica (igual à Home) preenchida com os dados que
// foram enviados ao cliente. Sem o template, cai numa bolha simples com os dados.
// Sem dados estruturados, cai no comportamento antigo (nome do template). O
// payload completo segue disponível no "VER PAYLOAD".
function MessageTemplatePreviewHiperApiV3({ message }) {
  const templatesByName = useContext(WabaTemplatesContext)
  const data = messagesLib.getWhatsappCloudApiTemplateData(message.message)

  if (!data || !data.templateName) {
    return <MessageSimpleTemplateHiperApiV3 message={message} />
  }

  const template = templatesByName?.[String(data.templateName).toLowerCase()]

  if (template) {
    return (
      <div>
        <Text className="font-light text-xs mb-2">COMO O CLIENTE RECEBE</Text>
        <TemplatePreviewBubble
          template={template}
          values={data.variables}
          buttonValues={data.buttons}
          paymentLink={message.link}
        />
        <Text className="block text-[10px] opacity-50 mt-1">Template: {data.templateName}</Text>
      </div>
    )
  }

  return <MessageTemplateDataBubble data={data} />
}

// Fallback (template não encontrado no snapshot): bolha simples com os dados
// enviados, rótulos inferidos, valor em destaque e botão de pagamento.
function MessageTemplateDataBubble({ data }) {
  const labels = messagesLib.getTemplateVariableLabels(data.templateName, data.variables.length)
  const valorIndex = labels.findIndex((label) => label === 'Valor')

  return (
    <div>
      <Text className="font-light text-xs mb-2">COMO O CLIENTE RECEBE</Text>
      <div className="rounded-2xl overflow-hidden border border-[#0000000d]" style={{ backgroundColor: '#202c33' }}>
        <div className="px-4 py-3 space-y-2">
          {data.variables.map((value, index) => {
            const isValor = index === valorIndex
            return (
              <div key={index} className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wide text-gray-400">
                  {labels[index]}
                </span>
                <span className={clsx('text-white break-words', { 'text-lg font-black text-emerald-400': isValor })}>
                  {isValor ? `R$ ${value}` : value}
                </span>
              </div>
            )
          })}
          {valorIndex === -1 && data.price && (
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wide text-gray-400">Valor</span>
              <span className="text-lg font-black text-emerald-400">{data.price}</span>
            </div>
          )}
        </div>
        {data.hasPaymentButton && (
          <div className="border-t border-[#ffffff1a] flex items-center justify-center space-x-2 py-3" style={{ color: '#53bdeb' }}>
            <PaymentsIcon style={{ fontSize: 18 }} />
            <span className="text-sm font-medium">Pagar minha fatura agora</span>
          </div>
        )}
      </div>
    </div>
  )
}

function MessageSimpleTemplateHiperApiV3({ message }) {
  const theme = useTheme()
  const templateName = messagesLib.getWhatsappCloudApiTemplateName(message.message)
  return (
    <div>
      <Text className="font-light text-xs">TEMPLATE ENVIADO</Text>
      {
        templateName
        ? <Text>{ templateName }</Text>
        :
        <div className="flex items-center space-x-2 pt-1">
          <ErrorIcon className={clsx({ 'text-red-600': !theme.isDark })} />
          <Text className={clsx('font-black', { 'text-red-600': !theme.isDark })}>Não identificado</Text>
        </div>
      }
    </div>
  )
}

// Só o botão: o drawer e o estado dele passaram para o MessageListItem, para que
// o atalho da linha e este botão abram exatamente a mesma tela de payload.
function MessageSimpleShowPayloadHiperApiV3({ onVerPayload }) {
  return (
    <div className="px-1 py-1">
      <button
        onClick={() => onVerPayload()}
        className="text-xs font-medium flex items-center space-x-2"
      >
        <SubtitlesIcon />
        <span>VER PAYLOAD</span>
      </button>
    </div>
  )
}

// Caixa de erro do relatório. Em instâncias Meta Oficial, traduz o erro da Meta
// para uma explicação em pt-br com ação sugerida; o texto técnico original fica
// atrás de "ver detalhe técnico". Em conexões Baileys (erro já em pt-br do
// backend), mostra o texto como está.
function MessageErrorBox({ erro }) {
  const { provider } = useContext(ApiDataWrapperContext)
  const [showTech, setShowTech] = useState(false)
  const isMeta = provider === 'hiperapi-v3-cloud-official'
  const friendly = isMeta ? translateMetaError(erro) : null

  if (!friendly) {
    return (
      <div className="bg-red-600 text-white p-1 flex space-x-2">
        <ReportProblemIcon />
        <span>{erro?.text}</span>
      </div>
    )
  }

  return (
    <div className="bg-red-600 text-white p-2">
      <div className="flex items-start space-x-2">
        <ReportProblemIcon />
        <div className="flex-1">
          <p className="font-black">{friendly.titulo}</p>
          <p className="text-sm opacity-90">{friendly.explicacao}</p>
          {friendly.acao && (
            <p className="text-sm mt-1">
              <span className="font-bold">O que fazer:</span> {friendly.acao}
            </p>
          )}
          {friendly.tecnico && (
            <>
              <MetaErrorLinks tecnico={friendly.tecnico} />
              <button
                onClick={() => setShowTech((v) => !v)}
                className="text-xs underline mt-1 opacity-80"
              >
                {showTech ? 'ocultar detalhe técnico' : 'ver detalhe técnico'}
              </button>
              {showTech && (
                <p className="text-xs mt-1 opacity-80 break-words">
                  {friendly.code ? `[${friendly.code}] ` : ''}
                  {friendly.tecnico}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function MessageFile({ file }) {
  return (
    <Message isFile={true}>
      <div className="flex space-x-2">
        <div>
          <div className="h-[40px] w-[35px] bg-red-500 rounded flex items-center justify-center px-1">
            <p className="font-black text-white text-xs">
              FILE
            </p>
          </div>
        </div>
        <div>
          <Text className="font-medium">
            {file.filename}
          </Text>
          <Text className="text-xs">
            Arquivo
          </Text>
        </div>
      </div>
    </Message>
  )
}

function ProgressDone() {
  return (
    <div className="relative">
      <div className="relative h-[10px] w-full bg-[#00ccff]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 shadow w-[40px] h-[40px] rounded-full bg-[#00ccff] flex justify-center items-center">
          <CheckIcon style={{ color: 'white' }} />
        </div>
      </div>
    </div>
  )
}

function ProgressWaiting() {
  const theme = useTheme()

  return (
    <div className="relative">
      <div
        className={clsx('relative h-[10px] w-full bg-opacity-[0.1]', {
          'bg-white': theme.isDark,
          'bg-gray-900': !theme.isDark
        })}
      >
        <div className={clsx('absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40px] h-[40px] rounded-full', {
          'shadow bg-[#67676a]': theme.isDark,
          'bg-[#cacaca]': !theme.isDark
        })}>

        </div>
      </div>
    </div>
  )
}

export function sanitizeSgpFileUrl(url) {
  const handledURL = String(url).startsWith('http') ?  url : `http://${url}`
  return handledURL.replace('/.pdf', '.pdf')
}
